import { createContext, useContext, useEffect, type ReactNode } from 'react';
import { useObservable } from '@cdn/state';
import type { Family } from './family';

const Context = createContext<Family | undefined>(undefined);

export function FamilyProvider({ family, children }: { family: Family; children: ReactNode }) {
	return <Context.Provider value={family}>{children}</Context.Provider>;
}

/**
 * The family bar's model, observed: the component re-renders when the descriptor changes
 */
export function useFamily(): Family {
	const family = useContext(Context);
	if (!family) throw new Error('useFamily needs a FamilyProvider');
	return useObservable(family);
}

/**
 * Declares the organization in view: `undefined` for a place without one, `null` to declare nothing because the
 * screen's own content declares its place (the home path's arrivals). Without a provider it does nothing.
 */
export function useOrganization(organization: string | undefined | null) {
	const family = useContext(Context);
	useEffect(() => {
		if (!family || organization === null) return;
		family.organization = organization;
	}, [family, organization]);
}

/**
 * Declares the common project a screen shows, for as long as the screen is mounted. The family bar then
 * names the project and its product menu keeps it. Without a provider (a screen rendered on its own in a
 * test) it does nothing.
 */
export function useProject(project: string | undefined) {
	const family = useContext(Context);
	useEffect(() => {
		if (!family) return;
		family.project = project;
		return () => {
			if (family.project === project) family.project = undefined;
		};
	}, [family, project]);
}
